/**
 * Sticky Add to Cart bar (custom element)
 * - Shown when primary ATC button scrolls out of view (Intersection Observer)
 * - Hidden when user scrolls back to primary ATC
 * - Submits main product form; syncs variant/quantity/disabled state from main form
 * - Respects theme settings: enable_mobile, enable_desktop
 * - Applies separate desktop/mobile display settings for title, quantity, and full-width
 * - Hides itself while the cart drawer is open
 * - Resets sticky button state after cart add completes (cartUpdate / cartError)
 * - Cleans up observer and subscriptions on disconnect
 * DOM placement: rendered inside <product-info> so that combined-listing full-page product swaps (which replace the entire <product-info> element) naturally destroy
 */
if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      static DESKTOP_BREAKPOINT_PX = 750;

      constructor() {
        super();
        this.intersectionObserver = null;
        this.unsubscribers = [];

        this._onResize = () => {
          if (!this._isBarEnabledForViewport()) {
            this._hide();
          } else {
            this._applyViewportDisplaySettings();
          }
        };
        this._onQuantityInputChange = () => this._syncQuantityFromMainForm();
        this._onProductInfoLoaded = () => this._syncQuantityFromMainForm();
      }

      connectedCallback() {
        this.elements = {
          primaryAtc: document.getElementById(this.dataset.primaryAtcId),
          productForm: document.getElementById(this.dataset.productFormId),
          mainQuantityInput: document.getElementById(this.dataset.quantityInputId),
          stickySubmit: this.querySelector('[data-sticky-atc-submit]'),
          stickyText: this.querySelector('.sticky-atc-bar__submit-text'),
          stickyQuantityInput: this.querySelector('[data-sticky-atc-qty]'),
          mainPrice: document.getElementById(`price-${this.dataset.sectionId}`),
          stickyPriceOutside: this.querySelector('.sticky-atc-bar__price--outside'),
          stickyPriceInButton: this.querySelector('.sticky-atc-bar__price--in-button'),
          stickyPriceSeparator: this.querySelector('.sticky-atc-bar__price-separator')
        };

        if (!this.elements.primaryAtc || !this.elements.productForm) return;

        this._setupQuantitySync();
        this._setupSubmitButton();
        this._setupIntersectionObserver();
        this._setupCartDrawerObserver();
        this._subscribeToEvents();

        if (this.elements.mainQuantityInput) {
          this.elements.mainQuantityInput.addEventListener('change', this._onQuantityInputChange);
        }

        window.addEventListener('resize', this._onResize);
        document.addEventListener('product-info:loaded', this._onProductInfoLoaded);
      }

      disconnectedCallback() {
        this.intersectionObserver?.disconnect();
        this.cartDrawerObserver?.disconnect();
        this.unsubscribers.forEach(unsub => unsub());
        this.unsubscribers = [];

        window.removeEventListener('resize', this._onResize);
        document.removeEventListener('product-info:loaded', this._onProductInfoLoaded);
        if (this.elements?.mainQuantityInput) {
          this.elements.mainQuantityInput.removeEventListener('change', this._onQuantityInputChange);
        }
      }

      _isBarEnabledForViewport() {
        const isDesktop = window.matchMedia(`(min-width: ${StickyAtcBar.DESKTOP_BREAKPOINT_PX}px)`).matches;
        return isDesktop ? this.dataset.enableDesktop === 'true' : this.dataset.enableMobile === 'true';
      }

      _isDesktop() {
        return window.matchMedia(`(min-width: ${StickyAtcBar.DESKTOP_BREAKPOINT_PX}px)`).matches;
      }

      _applyViewportDisplaySettings() {
        const isDesktop = this._isDesktop();

        const showTitle   = isDesktop ? this.dataset.showTitle    === 'true' : this.dataset.showTitleMobile    === 'true';
        const showQty     = isDesktop ? this.dataset.showQuantity === 'true' : this.dataset.showQuantityMobile === 'true';
        const isFullWidth = isDesktop ? this.dataset.fullWidth    === 'true' : this.dataset.fullWidthMobile    === 'true';

        const titleEl = this.querySelector('.sticky-atc-bar__title');
        if (titleEl) titleEl.classList.toggle('sticky-atc-bar__hidden', !showTitle);

        const qtyEl = this.querySelector('.sticky-atc-bar__quantity');
        if (qtyEl) qtyEl.classList.toggle('sticky-atc-bar__hidden', !showQty);

        const priceInButton = !showTitle;
        const { stickyPriceOutside, stickyPriceInButton, stickyPriceSeparator } = this.elements;
        if (stickyPriceOutside) stickyPriceOutside.classList.toggle('sticky-atc-bar__hidden', priceInButton);
        if (stickyPriceInButton) stickyPriceInButton.classList.toggle('sticky-atc-bar__hidden', !priceInButton);
        if (stickyPriceSeparator) stickyPriceSeparator.classList.toggle('sticky-atc-bar__hidden', !priceInButton);

        const innerEl = this.querySelector('.sticky-atc-bar__inner');
        this.classList.toggle('sticky-atc-bar--fit-content', !isFullWidth);
        if (innerEl) innerEl.classList.toggle('page-width', !isFullWidth);
      }

      syncSubmitState() {
        const { primaryAtc, stickySubmit, stickyText } = this.elements;
        if (!primaryAtc || !stickySubmit) return;

        const isDisabled = primaryAtc.hasAttribute('disabled') || primaryAtc.getAttribute('aria-disabled') === 'true';
        stickySubmit.disabled = isDisabled;

        const primarySpan = primaryAtc.querySelector('span');
        if (primarySpan && stickyText) stickyText.textContent = primarySpan.textContent.trim();

        const isLoading = primaryAtc.classList.contains('loading');
        stickySubmit.classList.toggle('loading', isLoading);

        const spinner = stickySubmit.querySelector('.loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !isLoading);
      }

      _syncQuantityToMainForm(value) {
        if (this.elements.mainQuantityInput) {
          this.elements.mainQuantityInput.value = value;
          this.elements.mainQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      _syncQuantityFromMainForm() {
        const { mainQuantityInput, stickyQuantityInput } = this.elements;
        if (mainQuantityInput && stickyQuantityInput) {
          const val = parseInt(mainQuantityInput.value, 10);
          if (!isNaN(val)) stickyQuantityInput.value = val;
        }
      }

      _setupQuantitySync() {
        const stickyInput = this.elements.stickyQuantityInput;
        if (!stickyInput) return;

        const min = parseInt(stickyInput.min, 10) || 1;
        const max = stickyInput.hasAttribute('max') ? parseInt(stickyInput.max, 10) : null;
        const step = parseInt(stickyInput.step, 10) || 1;

        const clamp = (val) => {
          let n = parseInt(val, 10);
          if (isNaN(n)) n = min;
          n = Math.max(min, n);
          if (max !== null) n = Math.min(max, n);
          if (step > 1) n = Math.round(n / step) * step;
          return n;
        };

        stickyInput.addEventListener('change', () => {
          stickyInput.value = clamp(stickyInput.value);
          this._syncQuantityToMainForm(stickyInput.value);
        });

        this.querySelectorAll('.sticky-atc-bar__qty-btn').forEach(btn => {
          btn.addEventListener('click', () => {
            const current = parseInt(stickyInput.value, 10) || min;
            const delta = btn.getAttribute('name') === 'plus' ? step : -step;
            stickyInput.value = clamp(current + delta);
            stickyInput.dispatchEvent(new Event('change', { bubbles: true }));
          });
        });
      }

      _setupSubmitButton() {
        const { stickySubmit, productForm } = this.elements;
        if (!stickySubmit) return;

        stickySubmit.addEventListener('click', () => {
          if (stickySubmit.disabled || stickySubmit.classList.contains('loading')) return;

          const mainSubmit = productForm.querySelector('[type="submit"]');
          if (mainSubmit && !mainSubmit.disabled) {
            mainSubmit.click();
            requestAnimationFrame(() => this.syncSubmitState());
          }
        });
      }

      _setupIntersectionObserver() {
        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach(entry => {
              entry.isIntersecting ? this._hide() : this._show();
            });
          },
          { threshold: 0 }
        );
        this.intersectionObserver.observe(this.elements.primaryAtc);
      }

      /**
       * Watches the cart-drawer element for the `active` class.
       * Hides the sticky bar while the drawer is open; restores it
       * (if the intersection observer still says it should be visible)
       * once the drawer closes.
       */
      _setupCartDrawerObserver() {
        const cartDrawer = document.querySelector('cart-drawer');
        if (!cartDrawer) return;

        this.cartDrawerObserver = new MutationObserver(() => {
          if (cartDrawer.classList.contains('active')) {
            this._hideForCartDrawer();
          } else {
            this._restoreAfterCartDrawer();
          }
        });

        this.cartDrawerObserver.observe(cartDrawer, {
          attributes: true,
          attributeFilter: ['class'],
        });
      }

      _hideForCartDrawer() {
        if (this._isDesktop()) return;
        this._cartDrawerOpen = true;
        this._hide();
      }

      _restoreAfterCartDrawer() {
        if (!this._cartDrawerOpen) return;
        this._cartDrawerOpen = false;
        if (this.elements?.primaryAtc) {
          const rect = this.elements.primaryAtc.getBoundingClientRect();
          const isOutOfView = rect.bottom < 0 || rect.top > window.innerHeight;
          if (isOutOfView) this._show();
        }
      }

      _subscribeToEvents() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        // Variant change sync
        this.unsubscribers.push(
          subscribe(PUB_SUB_EVENTS.variantChange, (payload) => {
            if (payload?.data?.sectionId !== this.dataset.sectionId) return;
            this.syncSubmitState();
            if (this.elements.mainPrice) {
              const html = this.elements.mainPrice.innerHTML;
              if (this.elements.stickyPriceOutside) this.elements.stickyPriceOutside.innerHTML = html;
              if (this.elements.stickyPriceInButton) this.elements.stickyPriceInButton.innerHTML = html;
            }
          })
        );

        // Cart state sync (unified handler)
        const handleCartChange = () => {
          setTimeout(() => this.syncSubmitState(), 0);
        };

        this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.cartUpdate, handleCartChange));
        this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.cartError, handleCartChange));
      }

      _show() {
        if (!this._isBarEnabledForViewport()) return;
        if (this._cartDrawerOpen) return;
        this._applyViewportDisplaySettings();
        this.removeAttribute('hidden');
        this.setAttribute('aria-hidden', 'false');
        this.syncSubmitState();
        this._syncQuantityFromMainForm();
      }

      _hide() {
        this.setAttribute('hidden', '');
        this.setAttribute('aria-hidden', 'true');
      }
    }
  );
}
